import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users, TrendingUp, TrendingDown, Wallet,
  AlertCircle, RefreshCw, IndianRupee, Gem,
  Clock, UserPlus, ChevronRight,
} from 'lucide-react';
import { dashboardAPI } from '../../api/dashboard';
import { useAuth } from '../../context/AuthContext';

// ── Helpers ───────────────────────────────────────────────────
const formatINR = (val) => {
  if (val === null || val === undefined) return '—';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency', currency: 'INR', maximumFractionDigits: 0,
  }).format(Number(val));
};

// ── Skeleton ──────────────────────────────────────────────────
function StatSkeleton() {
  return (
    <div className="card flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div className="skeleton h-8 w-8 rounded-xl" />
        <div className="skeleton h-3 w-16 rounded" />
      </div>
      <div className="skeleton h-6 w-28 rounded" />
      <div className="skeleton h-3 w-20 rounded" />
    </div>
  );
}

// ── Stat card ─────────────────────────────────────────────────
function StatCard({ icon: Icon, iconBg, iconColor, label, value, sub, subIcon: SubIcon, subColor, onClick }) {
  return (
    <div
      className={`card flex flex-col gap-3 ${onClick ? 'transition-all duration-200 active:scale-[0.98]' : ''}`}
      style={{ cursor: onClick ? 'pointer' : 'default' }}
      onClick={onClick}
    >
      <div className="flex items-center justify-between">
        <div className="flex h-9 w-9 items-center justify-center rounded-xl flex-shrink-0"
             style={{ background: iconBg }}>
          <Icon size={17} style={{ color: iconColor }} />
        </div>
        {onClick && <ChevronRight size={14} style={{ color: 'rgba(255,255,255,0.2)' }} />}
      </div>

      <div>
        <p className="text-xl font-bold text-white leading-tight">{value}</p>
        <p className="text-xs mt-0.5 font-medium" style={{ color: 'rgba(255,255,255,0.45)' }}>
          {label}
        </p>
      </div>

      {sub && (
        <div className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5"
             style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}>
          {SubIcon && <SubIcon size={11} style={{ color: subColor || 'rgba(255,255,255,0.35)' }} />}
          <p className="text-[11px]" style={{ color: subColor || 'rgba(255,255,255,0.4)' }}>{sub}</p>
        </div>
      )}
    </div>
  );
}

// ── Section divider ───────────────────────────────────────────
function SectionLabel({ label }) {
  return (
    <p className="px-1 text-[10px] font-semibold uppercase tracking-widest"
       style={{ color: 'rgba(255,255,255,0.3)' }}>
      {label}
    </p>
  );
}

// ── Main page ─────────────────────────────────────────────────
export default function DashboardPage() {
  const navigate = useNavigate();
  const { user }  = useAuth();

  const [stats,   setStats]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await dashboardAPI.getStats();
      if (data.status) setStats(data.data);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load dashboard stats.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  return (
    <div className="flex flex-col min-h-0">

      {/* ── Header ── */}
      <div className="page-header flex items-center justify-between">
        <div>
          <p className="font-serif text-base font-semibold text-white leading-tight">
            Welcome back{user?.name ? `, ${user.name}` : ''}
          </p>
          <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.4)' }}>
            Here's what's happening today
          </p>
        </div>
        <button
          onClick={fetchStats}
          disabled={loading}
          className="flex h-8 w-8 items-center justify-center rounded-xl transition-all active:scale-90 disabled:opacity-50"
          style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)' }}
          aria-label="Refresh"
        >
          <RefreshCw size={14} className={`text-white ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <div className="flex flex-col gap-4 p-4">

        {/* ── Error ── */}
        {error && (
          <div className="flex items-center gap-2 rounded-xl px-4 py-3 text-sm"
               style={{ background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', color: '#fca5a5' }}>
            <AlertCircle size={15} className="flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* ── Gold rate banner ── */}
        {loading ? (
          <div className="skeleton h-16 w-full rounded-2xl" />
        ) : stats?.gold_rate && (
          <div className="flex items-center gap-3 rounded-2xl px-4 py-3"
               style={{
                 background: 'linear-gradient(135deg,rgba(184,134,11,0.18) 0%,rgba(212,160,23,0.08) 100%)',
                 border: '1px solid rgba(212,160,23,0.25)',
               }}>
            <div className="flex h-9 w-9 items-center justify-center rounded-xl flex-shrink-0"
                 style={{ background: 'rgba(212,160,23,0.15)' }}>
              <Gem size={17} style={{ color: '#d4a017' }} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-semibold uppercase tracking-widest"
                 style={{ color: 'rgba(212,160,23,0.65)' }}>
                Current Gold Rate
              </p>
              <p className="text-lg font-bold leading-tight" style={{ color: '#f0c040' }}>
                {formatINR(stats.gold_rate.rate)}
                <span className="text-xs font-normal ml-1" style={{ color: 'rgba(212,160,23,0.6)' }}>/ gram</span>
              </p>
            </div>
            <p className="text-[11px] flex-shrink-0" style={{ color: 'rgba(255,255,255,0.35)' }}>
              {stats.gold_rate.date}
            </p>
          </div>
        )}

        {/* ── Users ── */}
        <SectionLabel label="Users" />
        <div className="grid grid-cols-2 gap-3">
          {loading ? (
            <><StatSkeleton /><StatSkeleton /></>
          ) : (
            <>
              <StatCard
                icon={Users}
                iconBg="rgba(99,102,241,0.15)"
                iconColor="#a5b4fc"
                label="Total Users"
                value={stats?.users?.total ?? '—'}
                sub={`+${stats?.users?.new_this_month ?? 0} this month`}
                subIcon={UserPlus}
                subColor="rgba(167,243,208,0.8)"
                onClick={() => navigate('/users')}
              />
              <StatCard
                icon={UserPlus}
                iconBg="rgba(52,211,153,0.12)"
                iconColor="#6ee7b7"
                label="New This Month"
                value={stats?.users?.new_this_month ?? '—'}
                onClick={() => navigate('/users')}
              />
            </>
          )}
        </div>

        {/* ── Deposits ── */}
        <SectionLabel label="Deposits" />
        <div className="grid grid-cols-2 gap-3">
          {loading ? (
            <><StatSkeleton /><StatSkeleton /></>
          ) : (
            <>
              <StatCard
                icon={TrendingUp}
                iconBg="rgba(52,211,153,0.12)"
                iconColor="#6ee7b7"
                label="Total Approved"
                value={formatINR(stats?.deposits?.total_approved_amount)}
                onClick={() => navigate('/investments')}
              />
              <StatCard
                icon={Clock}
                iconBg="rgba(251,191,36,0.12)"
                iconColor="#fcd34d"
                label="Pending Deposits"
                value={stats?.deposits?.pending_count ?? '—'}
                sub={`${formatINR(stats?.deposits?.pending_amount)} pending`}
                subIcon={IndianRupee}
                subColor="rgba(253,224,132,0.7)"
                onClick={() => navigate('/investments')}
              />
            </>
          )}
        </div>

        {/* ── Profit ── */}
        <SectionLabel label="Profit" />
        {loading ? (
          <StatSkeleton />
        ) : (
          <StatCard
            icon={Wallet}
            iconBg="rgba(212,160,23,0.12)"
            iconColor="#d4a017"
            label="Total Profit Distributed"
            value={formatINR(stats?.profit?.total_distributed)}
            onClick={() => navigate('/users')}
          />
        )}

        {/* ── Withdrawals ── */}
        <SectionLabel label="Withdrawals" />
        <div className="grid grid-cols-2 gap-3">
          {loading ? (
            <><StatSkeleton /><StatSkeleton /></>
          ) : (
            <>
              <StatCard
                icon={TrendingDown}
                iconBg="rgba(239,68,68,0.1)"
                iconColor="#fca5a5"
                label="Total Approved"
                value={formatINR(stats?.withdrawals?.total_approved_amount)}
                onClick={() => navigate('/withdrawals')}
              />
              <StatCard
                icon={Clock}
                iconBg="rgba(251,191,36,0.12)"
                iconColor="#fcd34d"
                label="Pending Withdrawals"
                value={stats?.withdrawals?.pending_count ?? '—'}
                sub={`${formatINR(stats?.withdrawals?.pending_amount)} pending`}
                subIcon={IndianRupee}
                subColor="rgba(253,224,132,0.7)"
                onClick={() => navigate('/withdrawals')}
              />
            </>
          )}
        </div>

      </div>
    </div>
  );
}
